<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<vl-layer-vector
  :visible="layer.visible"
  :extent="imageExtent"
  :update-while-interacting="false"
>

  <vl-source-vector
    ref="olSource"
    :loader-factory="loaderFactory"
    :strategy-factory="strategyFactory"
    url="-"
  > <!-- HACK because loader factory not used if URL not specified -->
    <vl-style-func :factory="styleFunctionFactory" />
  </vl-source-vector>

</vl-layer-vector>
</template>

<script>
import WKT from 'ol/format/WKT';
import {AnnotationCollection, Cytomine} from '@/api';
import {annotBelongsToLayer} from '@/utils/annotation-utils';
import {get} from '@/utils/store-helpers';
import constants from '@/utils/constants.js';

export default {
  name: 'annotation-layer',
  props: {
    index: String,
    layer: Object
  },
  data() {
    return {
      format: new WKT(),

      resolution: null,
      lastExtent: null,
      clustered: null,
      maxResolutionNoClusters: null,

      // Viewport raster plane (decided per view change by CytomineImage, broadcast as the
      // 'annotationPlane' event). Only the FIRST load waits for a decision: on a dense slide that
      // avoids fetching every annotation once before the picture takes over; afterwards the loader
      // runs incrementally exactly as before whenever the plane is 'vector'.
      rasterPlane: false,
      planePending: !!constants.ANNOTATION_RENDERER_ENABLED,
      pinnedIds: new Set(), // materialised on demand by a pick/selection; bounded by maxPins
      maxPins: 8
    };
  },
  computed: {
    project: get('currentProject/project'),
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },
    annotsIdsToSelect() {
      return this.imageWrapper.selectedFeatures.annotsToSelect.map(annot => annot.id);
    },
    imageExtent() {
      return [0, 0, this.image.width, this.image.height];
    },
    selectedFeatures() {
      return this.imageWrapper.selectedFeatures.selectedFeatures;
    },
    ongoingEdit() {
      return this.imageWrapper.draw.ongoingEdit;
    },
    terms() {
      return this.imageWrapper.style.terms || [];
    },
    styleFunctionFactory() {
      // Force computed property update when one of those properties change (leading to new style function =>
      // rerendering - see https://github.com/ghettovoice/vuelayers/issues/68#issuecomment-404223423)
      this.imageWrapper.selectedFeatures.selectedFeatures;
      this.imageWrapper.style.layersOpacity;
      this.terms.forEach(term => {
        term.visible;
        term.color;
        term.opacity;
      });
      this.imageWrapper.style.displayNoTerm;
      this.imageWrapper.style.noTermOpacity;
      this.imageWrapper.properties.selectedPropertyKey;
      this.imageWrapper.properties.selectedPropertyColor;
      this.imageWrapper.review.reviewMode;
      this.imageWrapper.style.wrappedTracks.forEach(track => {
        track.color;
      });

      return () => {
        return this.$store.getters[this.imageModule + 'genStyleFunction'];
      };
    },
    reviewMode() {
      return this.imageWrapper.review.reviewMode;
    }
  },
  watch: {
    reviewMode() { // in review mode, reviewed annotation no longer displayed => need to force reload
      this.clearFeatures();
    }
  },
  methods: {
    // Raster plane: drop every feature except the handful a pick/selection put there on purpose.
    pruneToPinned() {
      if (!this.$refs.olSource) {
        return;
      }
      const src = this.$refs.olSource.$source;
      src.getFeatures().filter(f => !this.pinnedIds.has(f.getId())).forEach(f => src.removeFeature(f));
    },

    // Pin an annotation so the raster-plane prune leaves it alone, keeping only the most recent
    // few (each pin holds full geometry). The current selection is never evicted.
    pinFeature(id) {
      this.pinnedIds.delete(id);
      this.pinnedIds.add(id);
      if (this.pinnedIds.size <= this.maxPins) {
        return;
      }
      const selected = new Set((this.selectedFeatures || []).map(f => f.id));
      for (const old of [...this.pinnedIds]) {
        if (this.pinnedIds.size <= this.maxPins) {
          break;
        }
        if (selected.has(old)) {
          continue;
        }
        this.pinnedIds.delete(old);
        const src = this.$refs.olSource && this.$refs.olSource.$source;
        const f = src && src.getFeatureById(old);
        if (f) {
          src.removeFeature(f);
        }
      }
    },

    // Clear the features AND OpenLayers' record of which extents are already loaded: it marks an
    // extent as loaded even when our loader returned early, so clearing features alone would
    // never bring the vectors back.
    forceReload() {
      if (!this.$refs.olSource) {
        return;
      }
      const src = this.$refs.olSource.$source;
      if (src && src.loadedExtentsRtree_) {
        src.loadedExtentsRtree_.clear();
      }
      this.clearFeatures();
    },

    // CytomineImage decides the plane and broadcasts it; this layer only obeys.
    onAnnotationPlane({index, plane}) {
      if (index !== this.index) {
        return; // another viewer's slide
      }
      if (plane === 'pending') {
        // the view is changing: hold loads until the plane for the new viewport is known
        this.planePending = true;
        this.armPendingTimer();
        return;
      }
      clearTimeout(this._planePendingTimer);
      const wasPending = this.planePending;
      this.planePending = false;

      // 'unavailable' = renderer down over a viewport too dense to load safely: hold the layer in
      // the same do-not-fetch state as the raster plane (CytomineImage tells the user why).
      const raster = plane === 'raster' || plane === 'unavailable';
      const changed = raster !== this.rasterPlane;
      this.rasterPlane = raster;
      if (raster) {
        this.suppressedLoad = null;
        this.pruneToPinned();
      } else if (changed) {
        // back from the picture: the source is empty and OpenLayers believes its extents are
        // loaded, so reset both and let it load the viewport
        this.pinnedIds.clear();
        this.suppressedLoad = null;
        this.forceReload();
      } else if (wasPending && this.suppressedLoad) {
        // still on vectors: run the load that was held back, INCREMENTALLY (no clearing, so
        // nothing on screen flickers; the reconcile below updates what is already there)
        const {extent, resolution} = this.suppressedLoad;
        this.suppressedLoad = null;
        this.loader(extent, resolution);
      }
    },

    // Never leave the layer permanently silent because a decision went missing.
    armPendingTimer() {
      clearTimeout(this._planePendingTimer);
      this._planePendingTimer = setTimeout(() => {
        if (this.planePending) {
          this.planePending = false;
          const held = this.suppressedLoad;
          this.suppressedLoad = null;
          if (held) {
            this.loader(held.extent, held.resolution);
          } else {
            this.forceReload();
          }
        }
      }, 5000);
    },

    clearFeatures(cache = true) {
      if (this.$refs.olSource) {
        this.$store.commit(this.imageModule + 'removeLayerFromSelectedFeatures', {layer: this.layer, cache});
        this.$refs.olSource.clearFeatures();
      }
    },

    annotBelongsToLayer(annot) {
      return annotBelongsToLayer(annot, this.layer, this.sliceIds);
    },

    addAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot) && this.$refs.olSource) {
        this.$refs.olSource.addFeature(this.createFeature(annot));
      }
    },
    selectAnnotationHandler({annot, index}) {
      // The layer check needs `annot.user`, which a selection coming from a raster pick may not
      // carry; the raster is already scoped to this layer server-side. On the raster plane this
      // layer holds no features, so materialise exactly this one and pin it, otherwise it could
      // never be selected or edited.
      const hasUser = Object.prototype.hasOwnProperty.call(annot, 'user');
      const mine = this.annotBelongsToLayer(annot) || (this.rasterPlane && !hasUser);
      if (index === this.index && mine && this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (!olFeature && this.rasterPlane && annot.location) {
          this.$refs.olSource.addFeature(this.createFeature(annot));
          olFeature = this.$refs.olSource.getFeatureById(annot.id);
        }
        if (this.rasterPlane) {
          this.pinFeature(annot.id);
        }
        if (!olFeature) {
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
        } else {
          this.$store.dispatch(this.imageModule + 'selectFeature', olFeature);
        }
      }
    },
    reloadAnnotationsHandler({idImage, clear = false, hard = false, fromProbe = false} = {}) {
      // The bare 10 s tick (no payload) used to refetch the whole viewport on every layer. With
      // the renderer enabled, CytomineImage first asks a 160-byte fingerprint probe and re-emits
      // this event with fromProbe=true only when something actually changed.
      if (constants.ANNOTATION_RENDERER_ENABLED && !idImage && !clear && !hard && !fromProbe) {
        return;
      }
      if (!idImage || idImage === this.image.id) {
        if (clear) {
          this.clearFeatures();
        } else if (hard) {
          this.clearFeatures(false);
          this.loader();
        } else {
          this.loader();
        }
      }
    },
    reviewAnnotationHandler(annot) {
      if (this.reviewMode) { // if the image is in review mode, reviewed annotation should no longer be displayed on user layer => call delete handler
        this.deleteAnnotationHandler(annot);
      }
    },
    editAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot) && this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (!olFeature) {
          return;
        }
        olFeature.setGeometry(this.format.readGeometry(annot.location));
        olFeature.set('annot', annot);

        let indexSelectedFeature = this.selectedFeatures.findIndex(ftr => ftr.id === annot.id);
        if (indexSelectedFeature >= 0) {
          this.$store.commit(this.imageModule + 'changeAnnotSelectedFeature', {
            indexFeature: indexSelectedFeature,
            annot
          });
        }
      }
    },
    deleteAnnotationHandler(annot) {
      if (this.annotBelongsToLayer(annot) && this.$refs.olSource) {
        let olFeature = this.$refs.olSource.getFeatureById(annot.id);
        if (!olFeature) {
          return;
        }
        this.$refs.olSource.removeFeature(olFeature);

        if (this.selectedFeatures.some(ftr => ftr.id === annot.id)) {
          this.$store.commit(this.imageModule + 'clearSelectedFeatures');
        }
      }
    },

    strategyFactory() {
      return (extent, resolution) => {
        this.lastExtent = extent;

        if (this.$refs.olSource && this.resolution && this.clustered !== null && ( // some features have already been loaded
          !this.clustered && resolution > this.maxResolutionNoClusters // recluster
                    || resolution !== this.resolution && this.clustered)) { // change of resolution while clustering

          // clear loaded extents to force reloading features
          this.$refs.olSource.$source.loadedExtentsRtree_.clear();
        }

        return [extent];
      };
    },

    async fetchAnnotations() {
      let annotations = (await Cytomine.instance.api.get(`/annotation-layers/${this.layer.id}/annotations`)).data;
      annotations.forEach((annotation) => {
        annotation.location = decodeURIComponent(atob(annotation.location));
        annotation.term = [];
        annotation.image = this.image.id;
        annotation.project = this.project.id;
      });

      return annotations;
    },
    async fetchAnnots(extent) {
      // OpenLayers/vuelayers can ask for the same extent twice within a few milliseconds
      // (measured: identical 370 KB requests 0.1 s apart). Share the in-flight request instead of
      // paying for it twice; both callers reconcile the same result.
      const inflightKey = `${extent.join()}|${this.reviewMode}`;
      this._inflight = this._inflight || new Map();
      if (this._inflight.has(inflightKey)) {
        return this._inflight.get(inflightKey);
      }
      const promise = this._fetchAnnots(extent).finally(() => this._inflight.delete(inflightKey));
      this._inflight.set(inflightKey, promise);
      return promise;
    },
    async _fetchAnnots(extent) {
      [0, 1].forEach(index => {
        if (extent[index] < 0) {
          extent[index] = 0;
        }
      });
      [2, 3].forEach(index => {
        if (this.imageExtent[index] < extent[index]) {
          extent[index] = this.imageExtent[index];
        }
      });

      let annots = await new AnnotationCollection({
        // 'user', not 'username': Core's search filters on 'user' (an id) and silently ignores
        // 'username', so every layer used to load EVERY annotation of the image in the viewport
        // (measured: 5,691 rows / 2.2 MB per layer on a dense slide where the layer owns them all,
        // and the same 2.2 MB again for every other visible layer that owns none).
        user: !this.layer.isReview ? this.layer.id : null,
        image: this.image.id,
        slices: this.sliceIds,
        reviewed: this.layer.isReview,
        notReviewedOnly: !this.layer.isReview && this.reviewMode,
        bbox: extent.join(),
        showWKT: true,
        showTerm: true,
        showGIS: true,
        showTrack: true,
        showLink: true,
        showImageGroup: true,
        // Core only clusters when a user filter is present. With the filter ignored (above) it
        // never clustered, so features were always drawn as features; keep that behaviour now
        // that the filter works, instead of suddenly showing k-means hulls at low zoom.
        kmeans: false
      }).fetchAll();

      return annots.array;
    },

    updateFeature(feature, annot) {
      let indexSelectedFeature = this.selectedFeatures.findIndex(ftr => ftr.id === feature.getId());
      let isFeatureSelected = indexSelectedFeature !== -1;

      if (!annot) {
        console.log(`Removing annot ${feature.getId()} in layer ${this.layer.id} (external action)`);
        this.$refs.olSource.removeFeature(feature);
        if (isFeatureSelected) {
          this.$store.commit(this.imageModule + 'clearSelectedFeatures');
        }
        return;
      }

      let storedAnnot = feature.get('annot');
      if (!this.clustered && annot.updated === storedAnnot.updated && this.sameTerms(annot.term, storedAnnot.term)) {
        // no modification performed since feature was loaded
        return;
      }

      if (isFeatureSelected) {
        if (this.ongoingEdit) {
          // if feature is selected and under modification, updating it may lead to conflict
          console.log(`Skipping update of selected annot ${annot.id} in layer ${this.layer.id} (ongoing edit)`);
          return;
        }
        console.log(`Updating selected annot ${annot.id} in layer ${this.layer.id} (external action)`);
        this.$store.commit(this.imageModule + 'changeAnnotSelectedFeature', {
          indexFeature: indexSelectedFeature,
          annot
        });
      }

      console.log(`Updating annot ${annot.id} in layer ${this.layer.id} (external action)`);
      feature.set('annot', annot);
      feature.setGeometry(this.format.readGeometry(annot.location));
    },

    async loader(extent = this.lastExtent, resolution = this.resolution) {
      this.resolution = resolution;

      if (!this.layer.visible || !extent) {
        return;
      }

      // Viewport raster plane. Before the first plane decision, do not fetch (a 5 s timer lifts
      // the gate if no decision ever arrives); while the picture is shown, the vector source stays
      // empty except for pinned selections.
      if (this.planePending) {
        this.suppressedLoad = {extent: extent.slice(), resolution};
        this.armPendingTimer();
        return;
      }
      if (this.rasterPlane) {
        this.pruneToPinned();
        return;
      }

      let arrayAnnots;
      this.$eventBus.$emit('annotationLoadingStart', {index: this.index}); // drives the "Annotations loading" indicator
      try {
        if (Object.prototype.hasOwnProperty.call(this.layer, 'username')) {
          arrayAnnots = await this.fetchAnnots(extent);
          // Order by size, so bigger ones are always sent to back
          arrayAnnots.sort(
            function (a, b) {
              if (a.area < b.area) {
                return 1;
              } else {
                return -1;
              }
            }
          );
        } else {
          arrayAnnots = await this.fetchAnnotations();
        }
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-fetch-annotations-viewer')});
        return;
      } finally {
        this.$eventBus.$emit('annotationLoadingEnd', {index: this.index});
      }

      if (!this.$refs.olSource) {
        return;
      }

      let wasClustered = this.clustered;
      if (arrayAnnots.length) {
        // 'count' is only present on clustered responses; on ordinary ones it is undefined and
        // 'undefined !== null' was true, so this latched clustered=true forever and the strategy
        // above then cleared loadedExtentsRtree_ on EVERY zoom step, refetching the whole layer.
        const firstCount = arrayAnnots[0].count;
        this.clustered = firstCount !== null && firstCount !== undefined;
        if (!this.clustered && resolution > this.maxResolutionNoClusters) {
          this.maxResolutionNoClusters = resolution;
        }
      }

      let annots = arrayAnnots.reduce((obj, annot) => {
        obj[annot.id] = annot;
        return obj;
      }, {});
      let seenAnnots = [];

      if (wasClustered !== null && wasClustered !== this.clustered) {
        this.clearFeatures(); // clearing features will retrigger the loader
      } else {
        let features = this.clustered ? this.$refs.olSource.$source.getFeatures()
          : this.$refs.olSource.$source.getFeaturesInExtent(extent);

        features.forEach(feature => {
          this.updateFeature(feature, annots[feature.getId()]);
          seenAnnots.push(feature.getId());
        });
      }

      arrayAnnots.forEach(annot => {
        if (!seenAnnots.includes(annot.id)) {
          this.$refs.olSource.addFeature(this.createFeature(annot));
        }
      });
    },

    loaderFactory() {
      return (extent, resolution) => this.loader(extent, resolution);
    },

    createFeature(annot) {
      let feature = this.format.readFeature(annot.location);
      feature.setId(annot.id);
      feature.set('annot', annot);

      if (this.annotsIdsToSelect.includes(annot.id)) {
        this.$store.dispatch(this.imageModule + 'selectFeature', feature);
      }

      return feature;
    },

    sameTerms(terms1, terms2) {
      if (terms1.length !== terms2.length) {
        return false;
      }
      return terms1.every(term => terms2.includes(term));
    }
  },
  mounted() {
    this.$eventBus.$on('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$on('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$on('selectAnnotationInLayer', this.selectAnnotationHandler);
    this.$eventBus.$on('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$on('reviewAnnotation', this.reviewAnnotationHandler);
    this.$eventBus.$on('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$on('deleteAnnotation', this.deleteAnnotationHandler);
  },
  beforeDestroy() {
    // unsubscribe from all events
    clearTimeout(this._planePendingTimer);
    this.$eventBus.$off('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$off('addAnnotation', this.addAnnotationHandler);
    this.$eventBus.$off('selectAnnotationInLayer', this.selectAnnotationHandler);
    this.$eventBus.$off('reloadAnnotations', this.reloadAnnotationsHandler);
    this.$eventBus.$off('reviewAnnotation', this.reviewAnnotationHandler);
    this.$eventBus.$off('editAnnotation', this.editAnnotationHandler);
    this.$eventBus.$off('deleteAnnotation', this.deleteAnnotationHandler);
  }
};
</script>
